import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Lock, ShieldQuestion } from "lucide-react";
import { RoomShell } from "@/components/RoomShell";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const navigate = useNavigate();
  const { user, checking } = useAuth();

  useEffect(() => {
    if (!checking && !user) navigate("/enter");
  }, [checking, user, navigate]);

  if (!user) return null;

  const badges = user.badges || [];
  const threshold = user.unlock_threshold || 3;

  return (
    <RoomShell testid="profile-page" kicker="Your Case File" title={user.name}>
      <div className="grid lg:grid-cols-3 gap-6 -mt-4">
        <div className="lg:col-span-1 rounded-md p-6" style={{ background: "var(--wood-dark)", border: "1px solid var(--wood-medium)" }}>
          <p className="font-typewriter text-[11px] uppercase tracking-[0.3em] text-[#D97706]">Investigator No.</p>
          <p className="font-serif-hq text-4xl text-[#FDF6E3] mt-1" data-testid="profile-number">{user.investigator_number}</p>
          <p className="text-sm text-[#A89A80] mt-1">{user.email}</p>

          <div className="mt-6 flex items-center gap-2">
            {user.official ? (
              <span className="flex items-center gap-2 font-typewriter text-xs uppercase tracking-widest text-[#4ade80]" data-testid="official-badge">
                <BadgeCheck size={16} /> Official Investigator
              </span>
            ) : (
              <span className="flex items-center gap-2 font-typewriter text-xs uppercase tracking-widest text-[#A89A80]">
                <ShieldQuestion size={16} /> Investigation in progress
              </span>
            )}
          </div>

          <div className="mt-6">
            <div className="flex justify-between mb-2">
              <span className="font-typewriter text-[10px] uppercase tracking-widest text-[#A89A80]">Broadcast unlock</span>
              <span className="font-typewriter text-[10px] text-[#F59E0B]">{badges.length}/{threshold}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "#2C1A0F" }}>
              <div className="h-full" style={{ width: `${Math.min(100, (badges.length / threshold) * 100)}%`, background: "#F59E0B" }} />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <p className="font-typewriter text-[11px] uppercase tracking-[0.3em] text-[#D97706] mb-4">Badges Earned</p>
          {badges.length === 0 ? (
            <div className="rounded-md p-10 text-center" style={{ background: "var(--wood-dark)", border: "1px dashed var(--wood-medium)" }}>
              <Lock size={32} className="text-[#2C1A0F] mx-auto mb-3" />
              <p className="text-[#A89A80]">No badges yet. Solve cases and run a Brain Scan.</p>
              <div className="flex gap-3 justify-center mt-5">
                <button onClick={() => navigate("/evidence")} className="rounded-md px-4 py-2 font-typewriter text-xs uppercase tracking-widest" style={{ background: "#991B1B", color: "#FDF6E3" }}>Evidence Room</button>
                <button onClick={() => navigate("/brain-scan")} className="rounded-md px-4 py-2 font-typewriter text-xs uppercase tracking-widest" style={{ border: "1px solid var(--wood-medium)", color: "#E6D5B8" }}>Brain Scan</button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="badge-grid">
              {badges.map((b, i) => (
                <motion.div key={b.key} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.06 }}
                  className="rounded-md p-5 flex items-center gap-4" style={{ background: "var(--wood-dark)", border: "1px solid rgba(245,158,11,0.3)" }}>
                  <span className="flex items-center justify-center w-11 h-11 rounded-full shrink-0" style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.5)" }}>
                    <BadgeCheck size={20} className="text-[#F59E0B]" />
                  </span>
                  <div>
                    <p className="font-serif-hq text-lg text-[#FDF6E3] leading-none">{b.label}</p>
                    <p className="font-typewriter text-[10px] uppercase tracking-widest text-[#A89A80] mt-1.5">Earned</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {user.tv_unlocked && !user.official && (
            <button onClick={() => navigate("/")} className="mt-6 w-full rounded-md py-3 font-typewriter text-sm uppercase tracking-widest" style={{ background: "#991B1B", color: "#FDF6E3" }}>
              Signal restored — return to the TV
            </button>
          )}
        </div>
      </div>
    </RoomShell>
  );
}
